/**
 * Where the browser paints the first-line baseline inside a `line-height: 1`
 * block. EditableTextBoxItem needs it to turn a PDF baseline y into a CSS `top`.
 *
 * CSS inline layout puts the baseline at `ascent + half-leading`, which at
 * line-height 1 works out to `0.5 + (A - D) / 2` em (A/D being the primary
 * font's ascent/descent over the em, i.e. canvas `measureText`'s
 * fontBoundingBoxAscent/Descent). The legacy fixed 0.8 is off by as much as
 * 0.11em, which is the per-block vertical jump you see when text switches from
 * the pdfjs canvas to HTML rendering.
 */

import { FontStyleFlags, cssFontShorthand } from '@/fonts/cssFont';

export const FALLBACK_BASELINE_RATIO = 0.8;

// Large sample size keeps the integer-ish metrics some browsers report precise.
const SAMPLE_SIZE_PX = 100;

const cache = new Map<string, number>();
const offsetCache = new Map<string, number>();

/**
 * Drops measured ratios/offsets. A stack measured before its primary face
 * loaded reflects the fallback font, so every font-load completion has to
 * flush this.
 */
export const clearBaselineRatioCache = (): void => {
  cache.clear();
  offsetCache.clear();
};

// Registered lazily on first measurement; document.fonts is absent under jsdom.
let invalidationListenerRegistered = false;
const ensureInvalidationListener = () => {
  if (invalidationListenerRegistered) return;
  if (typeof document === 'undefined' || !document.fonts?.addEventListener) return;
  document.fonts.addEventListener('loadingdone', clearBaselineRatioCache);
  invalidationListenerRegistered = true;
};

/**
 * Baseline ratio for a CSS font-family stack (first available font wins, same
 * as CSS line-box behavior). Cached per stack + style, fallbacks included.
 */
export const measureBaselineRatio = (fontFamily: string, style: FontStyleFlags = {}): number => {
  ensureInvalidationListener();

  const cssFont = cssFontShorthand(fontFamily, SAMPLE_SIZE_PX, style);

  const cached = cache.get(cssFont);
  if (cached !== undefined) return cached;

  let ratio = FALLBACK_BASELINE_RATIO;
  const ctx = document.createElement('canvas').getContext('2d');
  if (ctx) {
    ctx.font = cssFont;
    const metrics = ctx.measureText('');
    // The spec defines the descent as a distance, but be lenient about sign.
    const candidate =
      0.5 + (metrics.fontBoundingBoxAscent - Math.abs(metrics.fontBoundingBoxDescent)) / (2 * SAMPLE_SIZE_PX);
    if (Number.isFinite(candidate)) ratio = candidate;
  }

  cache.set(cssFont, ratio);
  return ratio;
};

/**
 * Lays out a hidden line-height:1 block and reads where the browser actually
 * put the first baseline: a zero-size inline-block sits exactly on it.
 *
 * This beats the canvas-metrics formula because it sees the engine's own line
 * metrics. Blink rounds ascent/descent to whole pixels before building the line
 * box, so the formula (exact in float space) lands a device pixel off about half
 * the time. Returns null where layout is inert, e.g. jsdom.
 */
const probeBaselineOffsetPx = (cssFont: string): number | null => {
  if (typeof document === 'undefined' || !document.body) return null;

  const div = document.createElement('div');
  div.style.cssText = 'position:absolute;visibility:hidden;left:-9999px;top:0;white-space:pre;';
  div.style.font = cssFont;
  // Must come after the font shorthand, which resets line-height to 'normal'.
  // The probe has to reproduce the text boxes' line box exactly.
  div.style.lineHeight = '1';
  const text = document.createElement('span');
  text.textContent = 'Hg';
  const probe = document.createElement('span');
  probe.style.cssText = 'display:inline-block;width:0;height:0;';
  div.append(text, probe);
  document.body.append(div);
  const offset = probe.getBoundingClientRect().top - div.getBoundingClientRect().top;
  div.remove();

  return Number.isFinite(offset) && offset > 0 ? offset : null;
};

/**
 * First-baseline offset (px below the box top) for a font stack at an exact
 * font size, i.e. what a `top = baselineY - offset` placement needs. Metric
 * rounding makes this non-linear in size, so the cache is per size. Falls back
 * to the canvas-metrics formula, then to 0.8em.
 */
export const measureBaselineOffset = (fontFamily: string, fontSizePx: number, style: FontStyleFlags = {}): number => {
  ensureInvalidationListener();

  const cssFont = cssFontShorthand(fontFamily, fontSizePx, style);

  const cached = offsetCache.get(cssFont);
  if (cached !== undefined) return cached;

  const offset = probeBaselineOffsetPx(cssFont) ?? measureBaselineRatio(fontFamily, style) * fontSizePx;

  offsetCache.set(cssFont, offset);
  return offset;
};
